import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en/translation.json';

/*
 * Only English is bundled. The other ten are fetched when something actually
 * asks for them.
 *
 * All eleven used to be static imports, which put 1.9MB of translation JSON
 * into main.js — every visitor downloaded Tamil, Telugu, Arabic and the rest
 * to read one language. English is the exception because it is the fallback:
 * i18next needs it present for any key a translation is missing.
 *
 * import() here is what makes webpack emit one chunk per locale.
 */
const LOADERS = {
  hi: () => import('./locales/hi/translation.json'),
  kn: () => import('./locales/kn/translation.json'),
  te: () => import('./locales/te/translation.json'),
  ta: () => import('./locales/ta/translation.json'),
  bn: () => import('./locales/bn/translation.json'),
  ar: () => import('./locales/ar/translation.json'),
  es: () => import('./locales/es/translation.json'),
  fr: () => import('./locales/fr/translation.json'),
  de: () => import('./locales/de/translation.json'),
  pt: () => import('./locales/pt/translation.json'),
};

// Single source of truth for the language picker (native names shown to users).
export const LANGUAGES = [
  { code: 'en', label: 'English',    native: 'English'  },
  { code: 'hi', label: 'Hindi',      native: 'हिन्दी'    },
  { code: 'kn', label: 'Kannada',    native: 'ಕನ್ನಡ'    },
  { code: 'te', label: 'Telugu',     native: 'తెలుగు'   },
  { code: 'ta', label: 'Tamil',      native: 'தமிழ்'    },
  { code: 'bn', label: 'Bengali',    native: 'বাংলা'    },
  { code: 'ar', label: 'Arabic',     native: 'العربية'  },
  { code: 'es', label: 'Spanish',    native: 'Español'  },
  { code: 'fr', label: 'French',     native: 'Français' },
  { code: 'de', label: 'German',     native: 'Deutsch'  },
  { code: 'pt', label: 'Portuguese', native: 'Português' },
];

// Right-to-left locales — drives <html dir="rtl">.
const RTL_LANGS = ['ar'];
const isRtl = (lng) => RTL_LANGS.includes((lng || '').split('-')[0]);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    fallbackLng: 'en',
    supportedLngs: LANGUAGES.map((l) => l.code),
    detection: {
      // Persisted choice wins; otherwise fall back to the browser language.
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'lxops_lang',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    returnEmptyString: false,
  });

// Keep <html lang> and text direction in sync (RTL for Arabic).
function applyHtmlLang(lng) {
  try {
    const root = document.documentElement;
    root.lang = lng;
    root.dir = isRtl(lng) ? 'rtl' : 'ltr';
  } catch { /* ignore */ }
}
i18n.on('languageChanged', applyHtmlLang);
applyHtmlLang(i18n.language || 'en');

/**
 * Makes sure a language's strings are present, fetching them if they are not.
 *
 * Safe to call with anything: English and an unknown code both resolve
 * immediately, and a language already loaded is not fetched twice. Callers
 * await this before changeLanguage so the switch happens in one repaint
 * rather than flashing English first.
 */
export async function ensureLanguage(code) {
  const lng = (code || '').split('-')[0];
  if (!lng || lng === 'en' || i18n.hasResourceBundle(lng, 'translation')) return;
  const load = LOADERS[lng];
  if (!load) return;
  try {
    const mod = await load();
    i18n.addResourceBundle(lng, 'translation', mod.default || mod, true, true);
  } catch {
    // A failed chunk leaves English in place, which is the fallback anyway.
  }
}

/*
 * The detector may have restored a non-English choice from localStorage before
 * its strings exist. Fetch them and re-announce the language so react-i18next
 * re-renders what is already on screen.
 */
const detected = (i18n.language || 'en').split('-')[0];
if (detected !== 'en' && LOADERS[detected]) {
  ensureLanguage(detected).then(() => i18n.changeLanguage(detected));
}

export default i18n;
